import { Dialog } from "@headlessui/react";
import clsx from "clsx";
import React, { FC } from "react";
import Draggable from "react-draggable";
import { twMerge } from "tailwind-merge";

interface DraggableModalProps {
  children: React.ReactNode;
  open: boolean;
  size?: "sm" | "sm-md" | "md" | "lg" | "confirm-modal";
  toggleModal: () => void;
}

interface CommonType {
  className?: string;
  children?: React.ReactNode;
}

type DraggableModalSub = {
  Header: typeof Header;
  Body: typeof Body;
  Footer: typeof Footer;
};

const Header: FC<CommonType> = ({ className, children }) => {
  return (
    <Dialog.Title
      className={clsx(
        "text-body-1 text-neutral-900 px-4 font-bold py-5 border-b border-neutral-400 relative",
        "md:text-sub-heading md:px-6",
        className
      )}
    >
      <div className="absolute w-full top-0 left-0 h-6 bg-transparent cursor-move drag-handle" />
      {children}
    </Dialog.Title>
  );
};

const Body: FC<CommonType> = ({ className, children }) => {
  return (
    <Dialog.Description
      as="div"
      className={twMerge("relative py-6 px-4", "md:py-8 md:px-6", className)}
    >
      <div className="absolute h-full w-6 drag-handle cursor-move left-0 top-0" />
      <div className="absolute h-full w-6 drag-handle cursor-move right-0 top-0" />
      <div className="absolute h-8 w-full drag-handle cursor-move left-0 top-0" />
      <div className="absolute h-8 w-full drag-handle cursor-move left-0 bottom-0" />
      {children}
    </Dialog.Description>
  );
};

const Footer: FC<CommonType> = ({ className, children }) => {
  return (
    <div
      className={clsx(
        "py-4 px-4 shadow-[0px_-3px_6px_0px_rgba(0,0,0,0.06)] relative",
        "md:px-6",
        className
      )}
    >
      <div className="absolute h-full w-6 drag-handle cursor-move left-0 top-0" />
      <div className="absolute h-full w-6 drag-handle cursor-move right-0 top-0" />
      <div className="absolute h-4 w-full drag-handle cursor-move left-0 top-0" />
      <div className="absolute h-4 w-full drag-handle cursor-move left-0 bottom-0" />
      {children}
    </div>
  );
};

const DraggableModal: FC<DraggableModalProps> & DraggableModalSub = ({
  open,
  toggleModal,
  children,
  size = "lg",
}) => {
  return (
    <Dialog open={open} onClose={() => toggleModal()} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Draggable handle=".drag-handle">
            <Dialog.Panel
              className={clsx("mx-auto rounded bg-white", {
                "w-full md:w-[25%]": size === "sm",
                "w-full md:w-[384px]": size === "sm-md",
                "w-full md:w-[50%]": size === "md",
                "w-full md:w-[75%]": size === "lg",
                "w-[512px]": size === "confirm-modal",
                "w-[1200px]": size === "detail",
              })}
            >
              {children}
            </Dialog.Panel>
          </Draggable>
        </div>
      </div>
    </Dialog>
  );
};

DraggableModal.Header = Header;
DraggableModal.Body = Body;
DraggableModal.Footer = Footer;

DraggableModal.displayName = "DraggableModal";
export { DraggableModal };
